/**
 * FloatingDock - Unified action dock for onboarding and Sage AI
 *
 * Houses both the progress checklist button and Sage AI chat button
 * in a floating dock at the bottom-right of the screen.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/lib/supabase/client';
import { cn } from '@/lib/utils';
import { Sparkles } from 'lucide-react';
import { ProgressButton } from './ProgressButton';
import { ChecklistPopup } from './ChecklistPopup';
import { SmartChat } from '@/components/sage/SmartChat';

interface FloatingDockProps {
  hideSage?: boolean;
  hideChecklist?: boolean;
}

const CHECKLIST_DISMISSED_KEY = 'settlewise-checklist-dismissed';

export function FloatingDock({ hideSage = false, hideChecklist = false }: FloatingDockProps) {
  const { user, profile } = useAuth();
  const { isFree, isPaid, getUsageStats } = useSubscription();

  const [checklistOpen, setChecklistOpen] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const totalCount = 7; // Total checklist items

  // Only show checklist for free client users who have completed onboarding (not advisors)
  const showChecklist = !hideChecklist && isFree && profile?.onboarding_complete && !isPaid && !profile?.is_advisor;

  // Calculate completion count
  useEffect(() => {
    if (!showChecklist || !user) return;

    const checkCompletion = async () => {
      let completed = 0;

      // 1. Financial Profile (has actual financial data)
      try {
        const [{ data: assets }, { data: debts }, { data: income }] = await Promise.all([
          supabase.from('assets').select('id').eq('user_id', user.id).limit(1),
          supabase.from('debts').select('id').eq('user_id', user.id).limit(1),
          supabase.from('income_sources').select('id').eq('user_id', user.id).limit(1),
        ]);
        if ((assets?.length ?? 0) > 0 || (debts?.length ?? 0) > 0 || (income?.length ?? 0) > 0) completed++;
      } catch (e) {
        console.error('Error checking financial data:', e);
      }

      // 2. Goals
      if (profile?.goals_complete) completed++;

      // 3. Spouse Profile
      try {
        const { data: spouseData } = await supabase
          .from('opponent_profiles')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        if (spouseData && spouseData.length > 0) completed++;
      } catch (e) {
        console.error('Error checking spouse profile:', e);
      }

      // 4. Documents
      const stats = getUsageStats();
      if (stats.documents.used > 0) completed++;

      // 5. Support Calculator
      if (stats.supportCalculations.used > 0) completed++;

      // 6. Strategy
      try {
        const { data: strategyData } = await supabase
          .from('strategies')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        if (strategyData && strategyData.length > 0) completed++;
      } catch (e) {
        console.error('Error checking strategy:', e);
      }

      // 7. Sage Conversations
      try {
        const { data: sageData } = await supabase
          .from('sage_conversations')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        if (sageData && sageData.length > 0) completed++;
      } catch (e) {
        console.error('Error checking sage:', e);
      }

      setCompletedCount(completed);

      // Auto-open checklist if there are incomplete items and hasn't been dismissed this session
      if (completed < totalCount && !hasAutoOpened) {
        const dismissed = sessionStorage.getItem(CHECKLIST_DISMISSED_KEY);
        if (!dismissed) {
          setChecklistOpen(true);
          setHasAutoOpened(true);
        }
      }
    };

    checkCompletion();
  }, [showChecklist, user, profile, getUsageStats, hasAutoOpened, totalCount]);

  // Track when user manually closes the checklist
  const handleClose = () => {
    setChecklistOpen(false);
    // Mark as dismissed for this session so it doesn't auto-open again
    sessionStorage.setItem(CHECKLIST_DISMISSED_KEY, 'true');
  };

  // Close checklist when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (checklistOpen && !target.closest('[data-dock]')) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [checklistOpen, handleClose]);

  // If nothing to show, return null
  if (hideSage && !showChecklist) return null;

  return (
    <div
      data-dock
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex items-end gap-2 sm:gap-3 pb-[env(safe-area-inset-bottom)]"
    >
      {/* Checklist Popup */}
      {showChecklist && (
        <div className="relative">
          <ChecklistPopup
            open={checklistOpen}
            onClose={handleClose}
            onItemClick={handleClose}
          />

          {/* Progress Button */}
          <ProgressButton
            completed={completedCount}
            total={totalCount}
            onClick={() => setChecklistOpen(!checklistOpen)}
            isActive={checklistOpen}
          />
        </div>
      )}

      {/* Sage AI Button - wrapped in error boundary so crashes don't take down the whole app */}
      {!hideSage && (
        <SmartChatSafe />
      )}
    </div>
  );
}

import React from 'react';

/** Error boundary that silently hides SmartChat if it crashes */
class SmartChatErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.error('[SmartChat] Caught render error:', error.message);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function SmartChatSafe() {
  return (
    <SmartChatErrorBoundary>
      <SmartChat hidden={false} docked />
    </SmartChatErrorBoundary>
  );
}

export default FloatingDock;
